import { addMinutes, format, parse, startOfDay } from 'date-fns';

export function parseTimeOnDay(baseDay, value) {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(
      baseDay.getFullYear(),
      baseDay.getMonth(),
      baseDay.getDate(),
      value.getHours(),
      value.getMinutes(),
      0,
      0
    );
  }
  const str = String(value).trim().toLowerCase();
  if (!str) return null;
  let d = parse(str, 'HH:mm', baseDay);
  if (!isNaN(d.getTime())) return d;
  d = parse(str, 'h:mm a', baseDay);
  if (!isNaN(d.getTime())) return d;
  d = parse(str, 'h:mm aa', baseDay);
  if (!isNaN(d.getTime())) return d;
  return null;
}

/**
 * @param {Date} date
 * @param {Record<string, any>} companyHours
 * @returns {{
 *   dayKey: string,
 *   dayHours: object,
 *   openTime: Date,
 *   closeTime: Date,
 *   breakStart: Date | null,
 *   breakEnd: Date | null,
 * } | null}
 */
export function getCompanyDayHoursForDate(date, companyHours = {}) {
  if (!date || !companyHours) return null;

  const dayKey = format(date, 'EEEE').toLowerCase();
  const dayHours = companyHours[dayKey];
  if (!dayHours?.isOpen) return null;

  const base = startOfDay(date);
  const openRaw = dayHours.openTime ?? dayHours.open ?? '09:00';
  const closeRaw = dayHours.closeTime ?? dayHours.close ?? '17:00';

  let openTime = parseTimeOnDay(base, openRaw);
  let closeTime = parseTimeOnDay(base, closeRaw);
  if (!openTime) openTime = parse('09:00', 'HH:mm', base);
  if (!closeTime) closeTime = parse('17:00', 'HH:mm', base);
  if (!openTime || !closeTime || closeTime <= openTime) return null;

  let breakStart = null;
  let breakEnd = null;
  if (dayHours.hasBreak && dayHours.breakStart && dayHours.breakEnd) {
    breakStart = parseTimeOnDay(base, dayHours.breakStart);
    breakEnd = parseTimeOnDay(base, dayHours.breakEnd);
    if (!breakStart || !breakEnd || breakEnd <= breakStart) {
      breakStart = null;
      breakEnd = null;
    }
  }

  return { dayKey, dayHours, openTime, closeTime, breakStart, breakEnd };
}

/**
 * True when [start, end) fits inside open hours and does not overlap a break.
 */
export function isRangeWithinCompanyHours(start, end, bounds) {
  if (!bounds?.openTime || !bounds?.closeTime || !start || !end) return false;
  if (start.getTime() < bounds.openTime.getTime()) return false;
  if (end.getTime() > bounds.closeTime.getTime()) return false;

  if (bounds.breakStart && bounds.breakEnd) {
    const overlapsBreak = start < bounds.breakEnd && end > bounds.breakStart;
    if (overlapsBreak) return false;
  }

  return true;
}

export function isDurationWithinCompanyHours(slotStart, durationMinutes, date, companyHours) {
  const bounds = getCompanyDayHoursForDate(date, companyHours);
  if (!bounds) return false;
  const end = addMinutes(slotStart, durationMinutes);
  return isRangeWithinCompanyHours(slotStart, end, bounds);
}
